import { SyntheticEvent, useCallback, useMemo } from 'react'
import { FieldFilterRestrictionOperator } from '@portal/pkg-api/gen/admin'

type ToStringable = {
  toString(): string
}

export type UseSelectSingleMultiProps = {
  value: string
  onChange: (newValue: string) => void
  operator: FieldFilterRestrictionOperator
}

export type UseSelectSingleMultiReturn<T extends ToStringable> = {
  calculatedValue: Array<T> | T | null
  handleChange: (event: SyntheticEvent, newValue: Array<T> | T | null) => void
  multiple: boolean
}

const MULTI_SELECT_OPERATOR = [
  FieldFilterRestrictionOperator.IN,
  FieldFilterRestrictionOperator.NOT_IN,
]

export function useSelectSingleMulti<T extends ToStringable>({
  value,
  onChange,
  operator,
}: UseSelectSingleMultiProps): UseSelectSingleMultiReturn<T> {
  const calculatedValue = useMemo(() => {
    const parsedValue = (() => {
      try {
        return JSON.parse(value) as Array<T>
      } catch {
        return value as unknown as T
      }
    })()

    if (MULTI_SELECT_OPERATOR.includes(operator)) {
      if (Array.isArray(parsedValue)) {
        return parsedValue
      } else if (parsedValue) {
        return [parsedValue]
      } else {
        return []
      }
    } else {
      if (Array.isArray(parsedValue)) {
        return parsedValue.length > 0 ? parsedValue[0] : null
      } else {
        return parsedValue || null
      }
    }
  }, [operator, value])

  const handleChange = useCallback(
    (_event: SyntheticEvent, newValue: Array<T> | T | null) => {
      if (MULTI_SELECT_OPERATOR.includes(operator)) {
        if (Array.isArray(newValue)) {
          onChange(JSON.stringify(newValue))
        } else {
          onChange(JSON.stringify([newValue]))
        }
      } else if (Array.isArray(newValue)) {
        onChange(newValue.length > 0 ? newValue[0].toString() : '')
      } else {
        onChange(newValue?.toString() || '')
      }
    },
    [onChange, operator],
  )

  const multiple = useMemo(() => MULTI_SELECT_OPERATOR.includes(operator), [operator])

  return { calculatedValue, handleChange, multiple }
}
